<script setup>
const props = defineProps({
  avatar: {
    type: String,
    default: null
  },
  displayName: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
  }
})

const sizeClasses = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-12 text-base',
  xl: 'size-16 text-lg'
}

const initials = computed(() => {
  if (!props.displayName) return '?'

  const names = props.displayName.trim().split(/\s+/)

  if (names.length === 1) {
    return names[0].charAt(0).toUpperCase()
  }

  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase()
})

// Colores de fondo basados en el displayName (estilo Google)
const backgroundColor = computed(() => {
  if (!props.displayName) return 'bg-slate-400'

  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500'
  ]

  // Generar un índice basado en el displayName
  let hash = 0
  for (let i = 0; i < props.displayName.length; i++) {
    hash = props.displayName.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
})

const containerClass = computed(() => {
  return [
    'rounded-full flex-shrink-0 flex items-center justify-center text-white font-medium overflow-hidden bg-gray-700',
    sizeClasses[props.size],
    props.avatar ? '' : backgroundColor
  ]
})

// Usar el sistema de imágenes para obtener URL pública del avatar
const { getImageUrl } = useImageUrl()

const avatarUrl = ref(null)

// Cargar URL del avatar cuando cambia la prop
watch(() => props.avatar, async (newAvatar) => {
  if (!newAvatar) {
    avatarUrl.value = null
    return
  }

  // Si ya es una URL completa, usarla directamente
  if (newAvatar.startsWith('http://') || newAvatar.startsWith('https://') || newAvatar.startsWith('data:')) {
    avatarUrl.value = newAvatar
    return
  }

  // Usar el sistema de imágenes con tipo 'user'
  avatarUrl.value = await getImageUrl(newAvatar, 'user')
}, { immediate: true })
</script>

<template>
  <div :class="containerClass">
    <img
      v-if="avatarUrl"
      :src="avatarUrl"
      :alt="displayName"
      class="w-full h-full object-cover"
    />
    <span v-else>{{ initials }}</span>
  </div>
</template>
